import { Epic, combineEpics, ofType } from 'redux-observable';
import { StoreState, SocialAuthProvider } from '../types';
import { switchMap, map, filter, startWith } from 'rxjs/operators';
import {
    SignInPageAction,
    SignInSocialButtonClickedAction,
    SIGNIN_SOCIAL_BUTTON_CLICKED,
    SIGNIN_SIGNING_IN,
    SIGNIN_SIGNED_IN,
    SIGNIN_SOCIAL_SIGNIN_FAILED_WRONG_PROVIDER,
    SIGNIN_SOCIAL_SIGNIN_FAILED,
    SIGNIN_EMAIL_SIGNIN_FAILED,
    SIGNIN_EMAIL_BUTTON_CLICKED,
    SIGNIN_RESET_PASSWORD_STARTED,
    SIGNIN_RESET_PASSWORD_SUCCESS,
    SIGNIN_RESET_PASSWORD_FAILED,
    SIGNIN_RESET_PASSWORD_BUTTON_CLICKED,
    SIGNIN_CREATE_ACCOUNT_FAILED,
    SIGNIN_CREATE_ACCOUNT_BUTTON_CLICKED,
    SIGNIN_SIGNIN_EMAIL_SENT,
    SIGNIN_SIGNIN_EMAIL_SEND_FAILED,
    SIGNIN_EMAIL_LINK_BUTTON_CLICKED,
    SIGNIN_VERIFY_EMAIL_LINK_FAILED,
    SIGNIN_EPIC_STARTED,
    SIGNIN_EPIC_FINISHED,
} from '../actions/SignInActions';
import { Observer, Observable } from 'rxjs';
import { firebaseAuth, googleProvider, facebookProvider } from '../firebase';
import { routerActions, RouterAction, LocationChangeAction, LOCATION_CHANGE } from 'connected-react-router';
import { Action } from 'redux';
import { t } from '../i18n';
import { USER_SIGNED_IN } from '../actions/AuthActions';

function getProvider(providerName: SocialAuthProvider) {
    switch (providerName) {
        case 'google':
            return googleProvider;
        case 'facebook':
            return facebookProvider;
        default:
            throw new Error(`unrecognised provider name ${providerName}`);
    }
}

const signInWithProvider = (buttonClick: SignInSocialButtonClickedAction): Observable<SignInPageAction> =>
    Observable.create(async (obs: Observer<SignInPageAction>) => {
        try {
            obs.next({ type: SIGNIN_EPIC_STARTED, epic: buttonClick.provider});
            obs.next({ type: SIGNIN_SIGNING_IN });
            const provider = getProvider(buttonClick.provider);
            await firebaseAuth.signInWithPopup(provider);
            obs.next({ type: SIGNIN_SIGNED_IN });
        } catch (err) {
            if (err.code === 'auth/account_exists_with_different_credential') {
                // TODO https://firebase.google.com/docs/auth/web/account-linking
                try {
                    const providers: string[] = await firebaseAuth.fetchSignInMethodsForEmail(err.email);
                    obs.next({ type: SIGNIN_SOCIAL_SIGNIN_FAILED_WRONG_PROVIDER, providers: providers, email: err.email });
                } catch {
                    obs.next({ type: SIGNIN_SOCIAL_SIGNIN_FAILED, message: err.message, code: err.code });
                }
            } else {
                obs.next({ type: SIGNIN_SOCIAL_SIGNIN_FAILED, message: err.message, code: err.code });
            }
        } finally {
            obs.next({ type: SIGNIN_EPIC_FINISHED });
        }
    });

const socialSignInEpic: Epic<SignInPageAction, SignInPageAction, StoreState> =
    (action$, store$) =>
        action$
            .pipe(
                ofType<SignInPageAction, SignInSocialButtonClickedAction>(SIGNIN_SOCIAL_BUTTON_CLICKED),
                switchMap(a => signInWithProvider(a))
            );

const signInWithEmail = (email: string, password: string): Observable<SignInPageAction> =>
    Observable.create(async (obs: Observer<SignInPageAction>) => {
        try {
            obs.next({ type: SIGNIN_EPIC_STARTED, epic: 'email-signin' });
            obs.next({ type: SIGNIN_SIGNING_IN });
            await firebaseAuth.signInWithEmailAndPassword(email, password);
            obs.next({ type: SIGNIN_SIGNED_IN });

        } catch (err) {
            obs.next({ type: SIGNIN_EMAIL_SIGNIN_FAILED, code: err.code, error: err.message });
        } finally {
            obs.next({ type: SIGNIN_EPIC_FINISHED });
        }
    });

const emailSignInEpic: Epic<SignInPageAction, SignInPageAction, StoreState> =
    (action$, store$) =>
        action$
            .pipe(
                ofType(SIGNIN_EMAIL_BUTTON_CLICKED),
                switchMap(() => signInWithEmail(store$.value.signin.email, store$.value.signin.password))
            );

const resetPassword = (email: string): Observable<SignInPageAction> =>
    Observable.create(async (obs: Observer<SignInPageAction>) => {
        try {
            obs.next({ type: SIGNIN_EPIC_STARTED, epic: 'reset-password' });
            obs.next({ type: SIGNIN_RESET_PASSWORD_STARTED });
            firebaseAuth.useDeviceLanguage();
            await firebaseAuth.sendPasswordResetEmail(email);
            obs.next({ type: SIGNIN_RESET_PASSWORD_SUCCESS });
        } catch (err) {
            console.error(err);
            obs.next({ type: SIGNIN_RESET_PASSWORD_FAILED, message: err.message });
        } finally {
            obs.next({ type: SIGNIN_EPIC_FINISHED });
        }
    });

const resetPasswordEpic: Epic<SignInPageAction, SignInPageAction, StoreState> =
    (action$, store$) =>
        action$
            .pipe(
                ofType(SIGNIN_RESET_PASSWORD_BUTTON_CLICKED),
                switchMap(() => resetPassword(store$.value.signin.email))
            );

const createEmailAccount = (email: string, password: string, displayName: string): Observable<SignInPageAction> =>
    Observable.create(async (obs: Observer<SignInPageAction>) => {
        try {
            obs.next({ type: SIGNIN_EPIC_STARTED, epic: 'create-account' });
            obs.next({ type: SIGNIN_SIGNING_IN });
            await firebaseAuth.createUserWithEmailAndPassword(email, password);
            if (firebaseAuth.currentUser && !firebaseAuth.currentUser.emailVerified) {
                await firebaseAuth.currentUser.sendEmailVerification();
            }
            if (firebaseAuth.currentUser) {
                await firebaseAuth.currentUser.updateProfile({ displayName, photoURL: null });
            }
            obs.next({ type: SIGNIN_SIGNED_IN });
        } catch (err) {
            console.error(err);
            obs.next({ type: SIGNIN_CREATE_ACCOUNT_FAILED, message: err.message });
        }
    });

const createEmailAccountEpic: Epic<SignInPageAction, SignInPageAction, StoreState> =
    (action$, store$) =>
        action$
            .pipe(
                ofType(SIGNIN_CREATE_ACCOUNT_BUTTON_CLICKED),
                switchMap(() => createEmailAccount(store$.value.signin.email, store$.value.signin.password, store$.value.signin.name))
            );

const sendSignInLink = (email: string): Observable<SignInPageAction> =>
    Observable.create(async (obs: Observer<SignInPageAction>) => {
        try {
            obs.next({ type: SIGNIN_EPIC_STARTED, epic: 'email-link' });

            if (email && email.length > 3) {
                // https://firebase.google.com/docs/auth/web/email-link-auth
                const actionCodeSettings = {
                    url: window.location.href,
                    handleCodeInApp: true
                };
                await firebaseAuth.sendSignInLinkToEmail(email, actionCodeSettings);
                window.localStorage.setItem('MAGIC_LINK_EMAIL', email);
                obs.next({ type: SIGNIN_SIGNIN_EMAIL_SENT });
            }
        } catch (err) {
            obs.next({ type: SIGNIN_SIGNIN_EMAIL_SEND_FAILED, message: err.message });
            console.error(err);
        } finally {
            obs.next({ type: SIGNIN_EPIC_FINISHED });
        }
    });

const sendSignInLinkEpic: Epic<SignInPageAction, SignInPageAction, StoreState> =
    (action$, store$) =>
        action$
            .pipe(
                ofType(SIGNIN_EMAIL_LINK_BUTTON_CLICKED),
                switchMap(() => sendSignInLink(store$.value.signin.email))
            );

const signInFromEmailLink = (): Observable<SignInPageAction> =>
    Observable.create(async (obs: Observer<SignInPageAction>) => {
        try {
            obs.next({ type: SIGNIN_EPIC_STARTED, epic: 'email-link' });

            const emailToConfirm =
                window.localStorage.getItem('MAGIC_LINK_EMAIL')
                || window.prompt(t('signin.provide-email-for-confirmation'))
                || null;

            if (!emailToConfirm) {
                return;
            }
            obs.next({ type: SIGNIN_SIGNING_IN });
            await firebaseAuth.signInWithEmailLink(emailToConfirm, window.location.href);
            obs.next({ type: SIGNIN_SIGNED_IN });
            window.localStorage.removeItem('MAGIC_LINK_EMAIL');
        } catch (error) {
            obs.next({ type: SIGNIN_VERIFY_EMAIL_LINK_FAILED, message: error.message });
        } finally {
            obs.next({ type: SIGNIN_EPIC_FINISHED });
        }
    });

const signInFromEmailLinkEpic: Epic<Action, SignInPageAction, StoreState> =
    (action$, store$) =>
        action$.pipe(
            ofType<Action, LocationChangeAction>(LOCATION_CHANGE),
            map(lc => lc.payload.location.pathname),
            startWith(window.location.pathname),
            filter(pathname => pathname === '/signin' && firebaseAuth.isSignInWithEmailLink(window.location.href)),
            switchMap(signInFromEmailLink)
        );

const redirectAfterSignInSuccessEpic: Epic<Action, RouterAction, StoreState> =
    (action$, store$) =>
        action$.pipe(
            ofType(USER_SIGNED_IN),
            filter(() => store$.value.router.location.pathname.startsWith('/signin')),
            map(() => routerActions.replace((store$.value.router.location.state as { from: string } || { from: '/account' }).from))
        );

export default combineEpics(
    emailSignInEpic,
    socialSignInEpic,
    resetPasswordEpic,
    redirectAfterSignInSuccessEpic,
    createEmailAccountEpic,
    sendSignInLinkEpic,
    signInFromEmailLinkEpic
);